import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { getDictionary, addWord, updateWord, deleteWord } from '../utils/dictionaryService';
import { supabase } from '../utils/supabaseClient';
import './DictionaryTest.css';
import { fetchOpenAIText } from '../utils/openaiClient';

export default function DictionaryProfile({ studentId, onError }) {
  const [dictionarySearchTerm, setDictionarySearchTerm] = useState('');
  const [isAddWordModalOpen, setIsAddWordModalOpen] = useState(false);
  const [newWord, setNewWord] = useState('');
  const [dictionaryEntries, setDictionaryEntries] = useState([]);
  const [isLoadingDictionary, setIsLoadingDictionary] = useState(true);
  const [editingWordId, setEditingWordId] = useState(null);
  const [editingWordData, setEditingWordData] = useState({
    word: '',
    level: '',
    translation: '',
    example: '',
    type: 'regular',
    partOfSpeech: null
  });
  const [wordVariants, setWordVariants] = useState([]);
  const [showVariantsModal, setShowVariantsModal] = useState(false);
  const [pendingWord, setPendingWord] = useState('');
  const [isLoadingVariants, setIsLoadingVariants] = useState(false);
  const [error, setError] = useState(null);
  const [selectedVariantIndices, setSelectedVariantIndices] = useState(new Set());

  // Функция нормализации ID - всегда возвращает UUID
  const normalizeStudentId = async (id) => {
    if (!id) return null;
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
    if (isUUID) return id;
    
    try {
      const { data } = await supabase
        .from('students')
        .select('id')
        .eq('invite_slug', id)
        .maybeSingle();
      return data?.id || id;
    } catch (error) {
      console.error('Error normalizing student ID:', error);
      return id;
    }
  };

  // Load dictionary from Supabase on mount and when studentId changes
  useEffect(() => {
    const loadDictionary = async () => {
      if (!studentId || studentId === 'new') {
        setDictionaryEntries([]);
        setIsLoadingDictionary(false);
        return;
      }

      setIsLoadingDictionary(true);
      try {
        const entries = await getDictionary(studentId);
        setDictionaryEntries(Array.isArray(entries) ? entries : []);
      } catch (error) {
        console.error('Error loading dictionary:', error);
        setDictionaryEntries([]);
      } finally {
        setIsLoadingDictionary(false);
      }
    };

    loadDictionary();

    // Listen for dictionary updates from other components
    const handleDictionaryUpdate = async (e) => {
      const updatedStudentId = e.detail?.studentId;
      const normalizedUpdatedId = await normalizeStudentId(updatedStudentId);
      const normalizedCurrentId = await normalizeStudentId(studentId);
      
      // Сравниваем нормализованные ID
      if (normalizedUpdatedId === normalizedCurrentId) {
        loadDictionary();
      }
    };
    
    window.addEventListener('dictionaryUpdated', handleDictionaryUpdate);
    
    return () => {
      window.removeEventListener('dictionaryUpdated', handleDictionaryUpdate);
    };
  }, [studentId]);

  const handleCloseAddWordModal = () => {
    if (!isLoadingVariants) {
      setIsAddWordModalOpen(false);
      setError(null);
      setNewWord('');
    }
  };

  const handleAddWord = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const trimmedWord = newWord.trim();
    if (!trimmedWord) {
      return;
    }
    
    // Protection against duplicate submission
    if (isLoadingVariants) {
      console.log('Already loading, ignoring duplicate submit');
      return;
    }
    
    if (!studentId || studentId === 'new') {
      console.error('Cannot add word: student ID not available');
      setError('Student ID not available');
      return;
    }
    
    setIsLoadingVariants(true);
    setError(null);

    const word = trimmedWord;
    console.log('🧪 Starting word test:', word);
    
    // First, request all possible parts of speech
    try {
      const variantsPrompt = `List all possible parts of speech for the word or phrase "${word}". For each meaning, determine:
1. Part of speech (noun, verb, adjective, adverb, phrasal verb, idiom, etc.)
2. Type: if it's a phrasal verb or idiom, indicate this in the "type" field
3. CEFR level (A1, A2, B1, B2, C1, or C2)
4. Russian translation - IMPORTANT: include ALL meanings and translation variants with detailed context in parentheses, do NOT shorten translations
5. Example sentence in English (8-15 words)

Respond ONLY with valid JSON in format:
{
  "variants": [
    {
      "partOfSpeech": "noun",
      "type": "regular",
      "level": "A1",
      "translation": "work (process of labor, occupation, activity)",
      "example": "I have a lot of work to do."
    },
    {
      "partOfSpeech": "phrasal verb",
      "type": "phrasal verb",
      "level": "B1",
      "translation": "work on something, develop",
      "example": "We need to work on this project."
    },
    {
      "partOfSpeech": "idiom",
      "type": "idiom",
      "level": "B2",
      "translation": "work like clockwork (work perfectly, without failures)",
      "example": "The new system works like a charm."
    }
  ]
}`;

      console.log('🔄 Requesting all parts of speech variants...');
      
      const content = await fetchOpenAIText({
        prompt: variantsPrompt,
        temperature: 0.7,
        maxTokens: 800,
      });
      
      if (!content) {
        throw new Error('No response from OpenAI API');
      }

      let contentStr = content.trim();
      
      // Parse variants
      let jsonStr = contentStr;
      if (jsonStr.startsWith('```json')) {
        jsonStr = jsonStr.replace(/^```json\s*/gi, '').replace(/\s*```/g, '');
      } else if (jsonStr.startsWith('```')) {
        jsonStr = jsonStr.replace(/^```\s*/g, '').replace(/\s*```/g, '');
      }
      
      const match = jsonStr.match(/\{[\s\S]*\}/);
      if (match) {
        const parsed = JSON.parse(match[0]);
        
        if (parsed.variants && Array.isArray(parsed.variants) && parsed.variants.length > 0) {
          // If multiple variants - show modal for selection
          if (parsed.variants.length > 1) {
            setWordVariants(parsed.variants);
            setPendingWord(word);
            setIsLoadingVariants(false);
            setIsAddWordModalOpen(false); // Close add word modal
            setNewWord('');
            setShowVariantsModal(true); // Show variant selection modal
            return;
      } else {
            // If only one variant - add immediately
            const variant = parsed.variants[0];
            await addWordVariant(variant, word, studentId);
            setIsLoadingVariants(false);
            setIsAddWordModalOpen(false); // Close modal after successful addition
            setNewWord('');
            return;
          }
        }
      }
      
      // If failed to parse variants, use old method
      console.warn('⚠️ Failed to get variants, using old method');
    } catch (err) {
      console.error('❌ Error getting variants:', err);
      setError(err.message || 'Error getting word variants');
      setIsLoadingVariants(false);
      // Modal stays open so user can see the error
      return;
    }

    // Old method (if failed to get variants)
    try {
      await handleSingleWord(word, studentId);
    } catch (err) {
      console.error('❌ Error in handleSingleWord:', err);
      setError(err.message || 'Error adding word');
    } finally {
      setIsLoadingVariants(false);
    }
  };

  const handleSingleWord = async (word, studentId) => {
    // Old method - single request for one value
    try {
      const prompt = `For the English word or phrase "${word}":
1. Determine if it is a phrasal verb, idiom, or regular word/phrase
2. Determine its CEFR level (A1, A2, B1, B2, C1, or C2) based on word frequency dictionaries
3. Provide its Russian translation - IMPORTANT: include ALL meanings and translation variants with detailed context in parentheses, do NOT shorten translations
4. Create a natural English example sentence (8-15 words)
5. Determine its part of speech (noun, verb, adjective, adverb, etc.)

Respond ONLY with valid JSON in this format: {"type": "regular/phrasal verb/idiom", "level": "X", "translation": "Y", "example": "Z", "partOfSpeech": "W"}`;

      console.log('🔄 Making request via proxy...');
      
      const content = await fetchOpenAIText({
        prompt,
        temperature: 0.7,
        maxTokens: 500,
      });
      
      if (!content) {
        throw new Error('No response from OpenAI API');
      }

      let contentStr = content.trim();
      console.log('✅ Raw response received:', contentStr);
      
      // Parse JSON from response
      let level = 'B1';
      let translation = 'Translation not available';
      let example = null;
      let type = 'regular';
      let partOfSpeech = null;
      
      try {
        let jsonStr = contentStr;
        if (jsonStr.startsWith('```json')) {
          jsonStr = jsonStr.replace(/^```json\s*/gi, '').replace(/\s*```/g, '');
        } else if (jsonStr.startsWith('```')) {
          jsonStr = jsonStr.replace(/^```\s*/g, '').replace(/\s*```/g, '');
        }
        
        const match = jsonStr.match(/\{[\s\S]*\}/);
        if (match) {
          const parsed = JSON.parse(match[0]);
          
          if (parsed.type && ['phrasal verb', 'idiom', 'regular'].includes(parsed.type.toLowerCase())) {
            type = parsed.type.toLowerCase();
          }
          
          if (parsed.level && /^[ABC]\d$/.test(parsed.level)) {
            level = parsed.level;
          }
          
          if (parsed.translation) {
            let translationText = parsed.translation.trim();
            if (Array.isArray(parsed.translation)) {
              translationText = parsed.translation
                .map(t => typeof t === 'string' ? t.trim() : String(t).trim())
                .filter(t => t.length > 0)
                .join(', ');
            } else if (typeof translationText === 'string') {
              translationText = translationText
                .replace(/\s+/g, ' ')
                .replace(/,\s*,/g, ',')
                  .trim();
              }
            if (translationText && translationText.length > 0) {
              translation = translationText;
            }
          }
          
          if (parsed.example && parsed.example.trim()) {
            example = parsed.example.trim();
          }

          if (parsed.partOfSpeech && parsed.partOfSpeech.trim()) {
            partOfSpeech = parsed.partOfSpeech.trim();
          }
            } else {
          translation = content;
        }
      } catch (parseErr) {
        console.error('❌ Error parsing JSON:', parseErr);
        translation = content;
      }
      
      await addWordVariant({ level, translation, example, type, partOfSpeech }, word, studentId);
      setIsLoadingVariants(false);
      setIsAddWordModalOpen(false); // Close modal after successful addition
      setNewWord('');
      setError(null);
    } catch (err) {
      const errorMsg = err.message || 'Error getting data';
      setError(errorMsg);
      setIsLoadingVariants(false);
      console.error('❌ Error testing word:', err);
      // Modal stays open so user can see the error
    }
  };

  const addWordVariant = async (variant, word, studentId) => {
    try {
      console.log('🔄 Adding word variant:', { word, variant, studentId });
      const newEntry = await addWord(studentId, {
        word: word,
        translation: variant.translation || 'Translation not available',
        level: variant.level || 'B1',
        example: variant.example || null,
        type: variant.type || 'regular',
        partOfSpeech: variant.partOfSpeech || null
      });
      
      console.log('✅ Word added successfully:', newEntry);
      
      // Reload dictionary from Supabase
      console.log('🔄 Reloading dictionary...');
      const entries = await getDictionary(studentId);
      console.log('✅ Dictionary reloaded, entries count:', entries.length);
      setDictionaryEntries(Array.isArray(entries) ? entries : []);

      // Событие уже отправлено в dictionaryService.js с нормализованным ID
    } catch (error) {
      console.error('❌ Error adding word variant:', error);
      const errorMessage = error.message || 'Failed to add word to dictionary';
      setError(errorMessage);
      if (onError) {
        onError({
          title: 'Error',
          message: `Failed to save word to Supabase: ${errorMessage}. Please check your connection and try again.`,
          type: 'error',
          confirmText: 'OK'
        });
      }
      throw error;
    }
  };

  const normalizePos = (pos) => {
    if (!pos) return null;
    const p = pos.toLowerCase().trim();
    if (p.includes('phrasal')) return 'phrasal verb';
    if (p.includes('idiom')) return 'idiom';
    if (p.includes('noun') || p.includes('substantive')) return 'noun';
    if (p.includes('verb')) return 'verb';
    if (p.includes('adjective') || p === 'adj') return 'adjective';
    if (p.includes('adverb') || p === 'adv') return 'adverb';
    if (p.includes('pronoun')) return 'pronoun';
    if (p.includes('preposition') || p === 'prep') return 'preposition';
    if (p.includes('conjunction') || p === 'conj') return 'conjunction';
    if (p.includes('interjection') || p.includes('exclamation')) return 'interjection';
    return p;
  };

  const translationWords = (text) => {
    if (!text) return new Set();
    return new Set(
      text.toLowerCase()
        .replace(/[()[\],.;:!?–—-]/g, ' ')
        .split(/\s+/)
        .filter(w => w.length > 2)
    );
  };

  const translationSimilarity = (a, b) => {
    const wa = translationWords(a);
    const wb = translationWords(b);
    if (!wa.size && !wb.size) return 1;
    if (!wa.size || !wb.size) return 0;
    const intersection = [...wa].filter(w => wb.has(w)).length;
    const union = new Set([...wa, ...wb]).size;
    return intersection / union;
  };

  const isVariantAlreadyAdded = (variant) => {
    const word = pendingWord.toLowerCase();
    const variantPos = normalizePos(variant.partOfSpeech);
    const variantType = variant.type || 'regular';
    return dictionaryEntries.some(entry => {
      if (entry.word?.toLowerCase() !== word) return false;
      // Primary: normalized part of speech match
      const entryPos = normalizePos(entry.partOfSpeech);
      if (entryPos && variantPos && entryPos === variantPos) return true;
      // Secondary: type match (phrasal verb / idiom / regular)
      if ((entry.type || 'regular') === variantType && variantType !== 'regular') return true;
      // Tertiary: translation similarity — catches AI inconsistency in labels
      if (translationSimilarity(entry.translation, variant.translation) >= 0.3) return true;
      return false;
    });
  };

  const closeVariantsModal = () => {
    setShowVariantsModal(false);
    setWordVariants([]);
    setPendingWord('');
    setSelectedVariantIndices(new Set());
  };

  const toggleVariantIndex = (idx) => {
    setSelectedVariantIndices(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const handleSelectSelected = async () => {
    const toAdd = wordVariants.filter((_, i) => selectedVariantIndices.has(i));
    for (const variant of toAdd) {
      await addWordVariant(variant, pendingWord, studentId);
    }
    closeVariantsModal();
  };

  const handleSelectAllVariants = async () => {
    for (const variant of wordVariants) {
      await addWordVariant(variant, pendingWord, studentId);
    }
    closeVariantsModal();
  };

  const handleDeleteWord = async (wordId) => {
    try {
      // Delete from Supabase
      await deleteWord(wordId);
      
      // Reload dictionary from Supabase
      const entries = await getDictionary(studentId);
      setDictionaryEntries(Array.isArray(entries) ? entries : []);
    } catch (error) {
      console.error('Error deleting word:', error);
      // In case of error, still remove from local state
      setDictionaryEntries(prev => prev.filter(entry => entry.id !== wordId));
    }
  };

  // Function to play word pronunciation
  const playWordAudio = (word) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(word);
      utterance.lang = 'en-US';
      utterance.rate = 0.8; // Slightly slower for better understanding
      utterance.pitch = 1;
      speechSynthesis.speak(utterance);
    } else {
      console.log('Speech synthesis not supported');
    }
  };

  const handleSaveEdit = async () => {
    if (!editingWordId) return;
    
    try {
      // Save to Supabase
      await updateWord(editingWordId, {
        word: editingWordData.word,
        translation: editingWordData.translation,
        example: editingWordData.example,
        level: editingWordData.level,
        type: editingWordData.type || 'regular',
        partOfSpeech: editingWordData.partOfSpeech || null
      });
      
      // Update local state
      setDictionaryEntries(prev =>
        prev.map(w =>
          w.id === editingWordId ? { ...w, ...editingWordData } : w
        )
      );
      setEditingWordId(null);
      
      // Reload dictionary to ensure consistency
      const entries = await getDictionary(studentId);
      setDictionaryEntries(Array.isArray(entries) ? entries : []);
    } catch (error) {
      console.error('Error updating word:', error);
      // In case of error, still update local state
      setDictionaryEntries(prev =>
        prev.map(w =>
          w.id === editingWordId ? { ...w, ...editingWordData } : w
        )
      );
      setEditingWordId(null);
    }
  };

  const filteredDictionary = dictionaryEntries.filter(entry =>
    (entry.word || '').toLowerCase().includes(dictionarySearchTerm.toLowerCase()) ||
    (entry.translation || '').toLowerCase().includes(dictionarySearchTerm.toLowerCase()) ||
    (entry.example || '').toLowerCase().includes(dictionarySearchTerm.toLowerCase())
  );
  
  // New words on top (already sorted by created_at in descending order from getDictionary)
  const displayedDictionary = [...filteredDictionary];

  return (
    <div className="section-card dictionary-section-content">
      <div className="dictionary-header">
        <div className="dictionary-search-bar">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="search-icon">
            <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill="currentColor"/>
          </svg>
          <input
            type="text"
            placeholder="Search dictionary..."
            value={dictionarySearchTerm}
            onChange={(e) => setDictionarySearchTerm(e.target.value)}
          />
        </div>
        <button className="add-new-words-btn dictionary-add-btn" onClick={() => setIsAddWordModalOpen(true)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4V20M4 12H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Add new words</span>
        </button>
      </div>

      {isAddWordModalOpen && ReactDOM.createPortal(
        <div
          className="modal-overlay"
          style={{ zIndex: 9999 }}
          onClick={(e) => {
            if (!isLoadingVariants && e.target === e.currentTarget) {
              handleCloseAddWordModal();
            }
          }}
        >
          <div className="modal-content" style={{ zIndex: 10000 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Word</h2>
              <button 
                className="close-modal-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isLoadingVariants) {
                    handleCloseAddWordModal();
                  }
                }}
                disabled={isLoadingVariants}
                style={{ opacity: isLoadingVariants ? 0.5 : 1, cursor: isLoadingVariants ? 'not-allowed' : 'pointer' }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <form 
              onSubmit={handleAddWord} 
              className="add-word-form"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="form-group">
                <input
                  type="text"
                  value={newWord}
                  onChange={(e) => setNewWord(e.target.value)}
                  placeholder="Enter word or phrase"
                  required
                  autoFocus
                  disabled={isLoadingVariants}
                  className="word-input"
                />
              </div>
              {isLoadingVariants && (
                <div style={{ textAlign: 'center', padding: '10px', color: '#666' }}>
                  <div style={{ display: 'inline-block', marginRight: '10px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeDasharray="31.416" strokeDashoffset="31.416" opacity="0.3"/>
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeDasharray="31.416" strokeDashoffset="23.562" strokeLinecap="round"/>
                    </svg>
                  </div>
                  Loading word details...
                </div>
              )}
              {error && !isLoadingVariants && (
                <div style={{ 
                  padding: '10px', 
                  margin: '10px 0', 
                  backgroundColor: '#fee', 
                  border: '1px solid #fcc', 
                  borderRadius: '4px', 
                  color: '#c33' 
                }}>
                  <strong>Error:</strong> {error}
                </div>
              )}
              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={handleCloseAddWordModal} 
                  className="cancel-btn"
                  disabled={isLoadingVariants}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={isLoadingVariants}
                >
                  {isLoadingVariants ? (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ display: 'inline-block', marginRight: '8px', animation: 'spin 1s linear infinite', verticalAlign: 'middle' }}>
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeDasharray="31.416" strokeDashoffset="31.416" opacity="0.3"/>
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeDasharray="31.416" strokeDashoffset="23.562" strokeLinecap="round"/>
                      </svg>
                      Loading...
                    </>
                  ) : (
                    'Add Word'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      <div className="dictionary-table-container">
        {isLoadingDictionary ? (
          <div className="dictionary-loading">
            <span className="dictionary-loading-spinner" aria-hidden />
            <span>Loading dictionary…</span>
          </div>
        ) : filteredDictionary.length > 0 ? (
          <>
            <div className="dictionary-table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Word</th>
                    <th>Part of Speech</th>
                    <th>Level</th>
                    <th>Translation</th>
                    <th>Example</th>
                    <th>Lesson</th>
                    <th style={{ textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedDictionary.map(entry => (
                    <tr key={entry.id}>
                      {editingWordId === entry.id ? (
                        <>
                          <td>
                            <input
                              value={editingWordData.word}
                              onChange={e => setEditingWordData({ ...editingWordData, word: e.target.value })}
                            />
                          </td>
                          <td>
                            <input
                              value={editingWordData.partOfSpeech || ''}
                              onChange={e => setEditingWordData({ ...editingWordData, partOfSpeech: e.target.value })}
                              placeholder="noun, verb, etc."
                            />
                          </td>
                          <td>
                            <input
                              value={editingWordData.level}
                              onChange={e => setEditingWordData({ ...editingWordData, level: e.target.value })}
                            />
                          </td>
                          <td>
                            <input
                              value={editingWordData.translation}
                              onChange={e => setEditingWordData({ ...editingWordData, translation: e.target.value })}
                            />
                          </td>
                          <td>
                            <input
                              value={editingWordData.example}
                              onChange={e => setEditingWordData({ ...editingWordData, example: e.target.value })}
                            />
                          </td>
                          <td>{entry.lesson?.title}</td>
                          <td className="dictionary-actions">
                            <button
                              className="student-action-btn student-save-btn"
                              title="Save"
                              onClick={handleSaveEdit}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="currentColor"/>
                              </svg>
                            </button>
                            <button
                              className="student-action-btn student-cancel-btn"
                              title="Cancel"
                              onClick={() => setEditingWordId(null)}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
                              </svg>
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <span>{entry.word}</span>
                              {entry.type && entry.type !== 'regular' && (
                                <span className={`dictionary-test-type-badge dictionary-test-type-${entry.type.replace(/\s+/g, '-').toLowerCase()}`} style={{ marginLeft: '8px' }}>
                                  {entry.type === 'phrasal verb' ? 'Phrasal verb' : entry.type === 'idiom' ? 'Idiom' : entry.type}
                                </span>
                              )}
                              <button
                                className="audio-play-btn"
                                title="Play pronunciation"
                                onClick={() => playWordAudio(entry.word)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  padding: '4px',
                                  borderRadius: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: '#6c757d',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.color = '#ee5a24';
                                  e.target.style.backgroundColor = 'rgba(238, 90, 36, 0.1)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.color = '#6c757d';
                                  e.target.style.backgroundColor = 'transparent';
                                }}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                                </svg>
                              </button>
                            </div>
                          </td>
                          <td>
                            {entry.partOfSpeech && (
                              <span className="dictionary-test-pos-badge">{entry.partOfSpeech}</span>
                            )}
                          </td>
                          <td>
                            <span className={`student-level-badge student-level-${(entry.level || 'na').toLowerCase()}`}>
                              {entry.level || 'N/A'}
                            </span>
                          </td>
                          <td>{entry.translation}</td>
                          <td>{entry.example}</td>
                          <td>{entry.lesson?.title}</td>
                          <td className="dictionary-actions">
                            <button
                              className="student-action-btn student-edit-btn"
                              title="Edit"
                              onClick={() => {
                                setEditingWordId(entry.id);
                                setEditingWordData({
                                  word: entry.word,
                                  level: entry.level,
                                  translation: entry.translation,
                                  example: entry.example,
                                  type: entry.type || 'regular',
                                  partOfSpeech: entry.partOfSpeech || null
                                });
                              }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 17.25V21H6.75L17.81 9.94L14.06 6.19L3 17.25ZM20.71 7.04C21.1 6.65 21.1 6.02 20.71 5.63L18.37 3.29C17.98 2.9 17.35 2.9 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04Z" fill="currentColor"/>
                              </svg>
                            </button>
                            
                            <button
                              className="student-action-btn student-delete-btn"
                              title="Delete"
                              onClick={() => handleDeleteWord(entry.id)}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19ZM8 9H16V19H8V9ZM15.5 4L14.5 3H9.5L8.5 4H5V6H19V4H15.5Z" fill="currentColor"/>
                              </svg>
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <p className="no-lessons-message">No dictionary entries found.</p>
        )}
      </div>

      {/* Modal for selecting part of speech variant */}
      {showVariantsModal && wordVariants.length > 0 && ReactDOM.createPortal(
        <div
          className="dictionary-test-modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2147483647
          }} 
          onClick={closeVariantsModal}
        >
          <div 
            className="dictionary-test-modal" 
            style={{ 
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              maxWidth: '700px',
              width: '90%',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              position: 'relative',
              zIndex: 2147483647
            }} 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dictionary-test-modal-header">
              <h2>Select variants for "{pendingWord}"</h2>
              <button className="dictionary-test-modal-close" onClick={closeVariantsModal}>×</button>
            </div>
            <div className="dictionary-test-modal-content">
              <div className="dictionary-test-variants-list">
                {wordVariants.map((variant, index) => {
                  const alreadyAdded = isVariantAlreadyAdded(variant);
                  const checked = selectedVariantIndices.has(index);
                  return (
                    <div
                      key={index}
                      className="dictionary-test-variant-item"
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 12,
                        cursor: alreadyAdded ? 'default' : 'pointer',
                        background: alreadyAdded ? '#f8fafc' : checked ? '#f0fdf4' : undefined,
                        borderColor: alreadyAdded ? '#e2e8f0' : checked ? '#22c55e' : undefined,
                        opacity: alreadyAdded ? 0.65 : 1,
                      }}
                      onClick={() => !alreadyAdded && toggleVariantIndex(index)}
                    >
                      <div style={{ marginTop: 2, flexShrink: 0 }}>
                        {alreadyAdded ? (
                          <span style={{ fontSize: 16, color: '#22c55e' }}>✓</span>
                        ) : (
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleVariantIndex(index)}
                            onClick={e => e.stopPropagation()}
                            style={{ width: 16, height: 16, accentColor: '#22c55e', cursor: 'pointer' }}
                          />
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div className="dictionary-test-variant-header">
                          {variant.type && variant.type !== 'regular' && (
                            <span className={`dictionary-test-type-badge dictionary-test-type-${variant.type.replace(/\s+/g, '-').toLowerCase()}`}>
                              {variant.type === 'phrasal verb' ? 'Phrasal verb' : variant.type === 'idiom' ? 'Idiom' : variant.type}
                            </span>
                          )}
                          <span className="dictionary-test-pos-badge">{variant.partOfSpeech || 'N/A'}</span>
                          <span className={`student-level-badge student-level-${(variant.level || 'na').toLowerCase()}`}>
                            {variant.level || 'N/A'}
                          </span>
                          {alreadyAdded && (
                            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#22c55e', fontWeight: 600, background: '#f0fdf4', borderRadius: 4, padding: '1px 6px', border: '1px solid #bbf7d0' }}>
                              Already added
                            </span>
                          )}
                        </div>
                        <div className="dictionary-test-variant-translation">
                          <strong>Translation:</strong> {variant.translation || 'N/A'}
                        </div>
                        <div className="dictionary-test-variant-example">
                          <strong>Example:</strong> {variant.example || 'N/A'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="dictionary-test-modal-actions" style={{ gap: 8 }}>
                <button
                  className="dictionary-test-button"
                  disabled={selectedVariantIndices.size === 0}
                  onClick={handleSelectSelected}
                  style={{ background: selectedVariantIndices.size > 0 ? '#22c55e' : '#d1d5db', color: '#fff', opacity: selectedVariantIndices.size > 0 ? 1 : 0.6 }}
                >
                  Add selected{selectedVariantIndices.size > 0 ? ` (${selectedVariantIndices.size})` : ''}
                </button>
                <button
                  className="dictionary-test-button dictionary-test-select-all-button"
                  onClick={handleSelectAllVariants}
                >
                  Add all
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
